/* Product form: stock add-to-cart flow (button spinner, drawer opens on the
   server render). Adds go through CartAPI's serialized queue; falls back to
   a direct request if cart-api.js is unavailable. */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.formInput = this.form?.querySelector("[name=id]");
        if (this.formInput) this.formInput.disabled = false;
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset.cartType || "page";

        this.submitButton = this.querySelector('[type="submit"]');
        this.body = document.querySelector("body");

        this.submitButton?.addEventListener("click", (e) => {
          e.preventDefault();
          this.form?.requestSubmit();
        });

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.handleErrorMessage = this.handleErrorMessage.bind(this);
      }

      /* Same product already in the cart: bump its row instantly (no spinner wait) */
      bumpExistingRow() {
        try {
          if (this.cartType !== "drawer" || !this.cart) return false;
          if (this.closest("quick-add-modal")) return false;
          if (this.dataset.instantCheckout === "true") return false;
          const vid = this.formInput ? String(this.formInput.value) : "";
          if (!vid) return false;
          let row = null;
          this.cart
            .querySelectorAll(".wt-cart__item")
            .forEach((r) => {
              if (row) return;
              r.querySelectorAll('a[href*="variant="]').forEach((a) => {
                const m = (a.getAttribute("href") || "").match(
                  /[?&]variant=(\d+)/
                );
                if (m && m[1] === vid) row = r;
              });
            });
          if (!row && location.pathname.indexOf("/products/") === 0) {
            this.cart
              .querySelectorAll(".wt-cart__item")
              .forEach((r) => {
                if (row) return;
                const a = r.querySelector('a[href*="/products/"]');
                if (!a) return;
                const href = a.getAttribute("href") || "";
                if (/[?&]variant=/.test(href)) return;
                try {
                  if (
                    new URL(a.href, location.origin).pathname ===
                    location.pathname
                  )
                    row = r;
                } catch (e) {}
              });
          }
          if (!row) return false;
          const input = row.querySelector(".js-counter-quantity");
          if (!input) return false;
          const addQty =
            parseInt(this.form.querySelector('[name="quantity"]')?.value, 10) ||
            1;
          const cur = parseInt(input.value, 10) || 0;
          let next = cur + addQty;
          const max = parseInt(input.getAttribute("max"), 10);
          if (max && next > max) next = max;
          if (next === cur) return false;
          input.value = next;
          input.dispatchEvent(new Event("change", { bubbles: true }));
          if (!document.body.classList.contains("page-overlay-cart-on")) {
            this.cart.toggleDrawerClasses();
          }
          return true;
        } catch (e) {
          return false;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.cart?.setActiveElement(document.activeElement);

        if (this.bumpExistingRow()) {
          const bl = this.querySelector(".loading-overlay__spinner");
          this.submitButton.classList.add("loading");
          if (bl) bl.classList.remove("hidden");
          setTimeout(() => {
            this.submitButton.classList.remove("loading");
            this.submitButton.removeAttribute("aria-disabled");
            if (bl) bl.classList.add("hidden");
          }, 350);
          return;
        }

        const loader = this.querySelector(".loading-overlay__spinner");
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (loader) loader.classList.remove("hidden");

        setTimeout(() => {
          const formData = new FormData(this.form);

          const doAdd = window.CartAPI
            ? () => window.CartAPI.add(formData)
            : () => {
                formData.set("sections", "cart-drawer,cart-icon-bubble");
                formData.set("sections_url", location.pathname);
                const cfg =
                  typeof fetchConfig === "function"
                    ? fetchConfig("javascript")
                    : { headers: {} };
                const headers = Object.assign({}, cfg.headers, {
                  "X-Requested-With": "XMLHttpRequest",
                });
                delete headers["Content-Type"];
                return fetch(
                  (window.routes && routes.cart_add_url) || "/cart/add",
                  { method: "POST", headers, body: formData },
                )
                  .then((r) => r.json())
                  .then((j) => {
                    if (j.status) {
                      const e = new Error("add failed");
                      e.data = j;
                      throw e;
                    }
                    const d = document.querySelector("cart-drawer");
                    if (d && j.sections)
                      d.renderContents({ sections: j.sections }, false);
                    else
                      document.dispatchEvent(
                        new CustomEvent("cart-drawer:refresh"),
                      );
                    return j;
                  });
              };

          doAdd()
            .then((response) => {
              if (this.dataset.instantCheckout === "true") {
                window.location = "/checkout";
                return;
              }
              if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
              const quickAddModal = this.closest("quick-add-modal");
              if (quickAddModal) {
                document.body.addEventListener(
                  "modalClosed",
                  () =>
                    setTimeout(() => {
                      if (
                        this.cartType === "drawer" &&
                        !document.body.classList.contains(
                          "page-overlay-cart-on",
                        )
                      ) {
                        this.cart.toggleDrawerClasses();
                      }
                    }),
                  { once: true },
                );
                if (this.cartType === "drawer") quickAddModal.hide(true);
              }
              if (
                !quickAddModal &&
                this.cartType === "drawer" &&
                !document.body.classList.contains("page-overlay-cart-on")
              ) {
                this.cart.toggleDrawerClasses();
              }
            })
            .catch((e) => {
              const data = e && e.data;
              if (data) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: "product-form",
                  productVariantId: formData.get("id"),
                  errors: data.errors || data.description,
                  message: data.message,
                });
                this.handleErrorMessage(data.description);
                const soldOutMessage =
                  this.submitButton.querySelector(".sold-out-message");
                if (soldOutMessage) {
                  this.submitButton.setAttribute("aria-disabled", true);
                  this.submitButton
                    .querySelector("span")
                    .classList.add("hidden");
                  soldOutMessage.classList.remove("hidden");
                }
              } else {
                console.error(e);
              }
              document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
            })
            .finally(() => {
              this.submitButton.removeAttribute("aria-disabled");

              const qbContainer = document.querySelector(
                ".wt__quick-buy__container",
              );
              if (qbContainer && !qbContainer.classList.contains("hidden")) {
                qbContainer.querySelector(".wt-product")?.remove();
                qbContainer.classList.remove("wt__quick-buy__container--open");
                document
                  .querySelector(".wt__quick-buy--page-overlay")
                  ?.classList.remove("wt__quick-buy--page-overlay--open");
                document.body.classList.remove("quick-buy-page-overlay");
                qbContainer
                  .querySelector(".wt__quick-buy-loader")
                  ?.classList.remove("hidden");
              }

              setTimeout(() => {
                this.submitButton.classList.remove("loading");
                this.querySelector(".loading-overlay__spinner")?.classList.add(
                  "hidden",
                );
              }, 200);
            });
        }, 0);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    },
  );
}